import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IAnnotationsStore } from '../../../annotations.store';
import { IAnnotationUiStore } from '../../../annotationUi.store';
import { IAnnotationTypeBl } from '../../annotationTypes/annotationType.bl';
import { IAnnotationReviewStore } from '../../review/annotationReview.store';
import { ReviewMode } from '../../review/models/reviewMode';
import { IProject, ObjectsListContainer } from '../components/objectsList/ObjectsList.container';
import { ProjectPicker } from '../components/projectPicker/ProjectPicker';
import { VerticalMenu, VerticalMenuItem } from '../components/verticalMenu/VerticalMenu';
import { ImageSegmentations } from '../segmentationContextMenu/imageSegmentations.context';

interface IProps {
  isReadOnly: boolean;
  isReview: boolean;
  showObjectsList: boolean;
  currentProjectId?: string;
  defaultTabOpened?: VerticalMenuItem;
  projectPickerEmptyListLabel?: TranslationKey;
}

export const LeftMenuContainer = observer((props: IProps): ReactElement => {
  const annotationReviewStore = useInjection(IAnnotationReviewStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationUiStore = useInjection(IAnnotationUiStore.$);

  const { projectId } = useParams<{ projectId: string }>();
  const context = useContext(ImageSegmentations);
  const [activeTab, setActiveTab] = useState<VerticalMenuItem | undefined>(props.defaultTabOpened);

  useEffect(() => {
    annotationUiStore.setIsLeftMenuOpened(props.defaultTabOpened !== undefined);
    annotationsStore.setHiddenSegmentations([]);
    annotationTypeBl.unhideAllAnnotationTypeOptions(projectId);
  }, [props.defaultTabOpened, projectId]);

  const handleProjectsPicked = (projectIds: string[]): void => {
    context.setSelectedProjects(projectIds);
  };

  const handleOpenedChanged = (projectIds: string[]): void => {
    context.setOpenedProjects(projectIds);
  };

  const handleClose = (): void => {
    toggle();
  };

  const getOptions = (): IProject[] => {
    return Object.entries(context.segmentations)
      .map(([pId, segmentations]) => {
        const project = context.projects.find((p) => p.id === pId);
        if (project) {
          return {
            ...project,
            count: segmentations.length,
            questions: context.answeredQuestions[pId],
          };
        }
        return undefined;
      })
      .filter((project): project is IProject => project !== undefined && project.id !== projectId)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  };

  const toggle = (menuItem?: VerticalMenuItem): void => {
    const newActiveTab = activeTab === menuItem ? undefined : menuItem;
    setActiveTab(newActiveTab);
    annotationUiStore.setIsLeftMenuOpened(newActiveTab !== undefined);
    context.toggleTab(menuItem);
  };

  const isReadonly = !(props.isReview && annotationReviewStore.currentMode === ReviewMode.Correct) && props.isReadOnly;

  return (
    <div className='left-menu-container'>
      <VerticalMenu
        toggle={toggle}
        activeTab={activeTab}
        showObjectsList={props.showObjectsList}
      />
      {activeTab === VerticalMenuItem.ANNOTATED_IN_PROJECTS && (
        <ProjectPicker
          options={getOptions()}
          selected={context.selectedProjects}
          opened={context.openedProjects}
          onChange={handleProjectsPicked}
          onOpen={handleOpenedChanged}
          onClose={handleClose}
          isLoaded={context.isLoaded}
          emptyListLabel={props.projectPickerEmptyListLabel}
        />
      )}
      {props.showObjectsList && activeTab === VerticalMenuItem.LIST_OF_OBJECTS && (
        <ObjectsListContainer
          onClose={handleClose}
          isReadOnly={isReadonly}
        />
      )}
    </div>
  );
});
